import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { NavBar } from '../components/NavBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inputStyle = {
  background: '#070B14', border: '1px solid rgba(245,230,211,0.15)',
  borderRadius: '10px', color: '#F5E6D3', fontFamily: 'Montserrat,sans-serif',
  fontSize: '0.9rem', padding: '13px 16px', outline: 'none', width: '100%',
  boxSizing: 'border-box', transition: 'border-color 0.2s',
};

export default function ClaimPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';

  const [form, setForm] = useState({ email: prefillEmail, allinx_id: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { success, first_name, result_value, is_jackpot }
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.allinx_id.trim()) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${BACKEND_URL}/api/gewinnspiel/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, allinx_id: form.allinx_id.trim() }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setResult(data);
      } else {
        const detail = data.detail || 'error_generic';
        if (detail === 'email_not_found') setError(t('claim.error_not_found'));
        else if (detail === 'already_claimed') setError(t('claim.error_already_claimed'));
        else if (detail === 'allinx_id_already_used') setError(t('claim.error_allinx_used'));
        else setError(t('claim.error_generic'));
      }
    } catch {
      setError(t('claim.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#05080F', color: '#F5E6D3',
      fontFamily: 'Montserrat,sans-serif',
      backgroundImage: 'url(https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/w9k3vexk_sunset%20palms%20v2.jpg)',
      backgroundSize: 'cover', backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
    }}>
      {/* Dark overlay */}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,8,15,0.78)', zIndex: 0 }} />

      {/* Navbar */}
      <NavBar />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '120px 20px 60px' }}>

        {/* Card */}
        <div style={{
          background: 'rgba(7, 11, 20, 0.92)',
          border: '1px solid rgba(212,175,55,0.3)',
          borderRadius: '24px',
          padding: 'clamp(28px, 5vw, 52px)',
          width: '100%', maxWidth: '480px',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
        }}>
          {!result ? (
            <>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🏆</div>
                <h1 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', color: '#D4AF37', margin: '0 0 10px', lineHeight: 1.2 }}>
                  {t('claim.title')}
                </h1>
                <p style={{ color: 'rgba(245,230,211,0.55)', fontSize: '0.88rem', lineHeight: 1.6, margin: 0 }}>
                  {t('claim.subtitle')}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(245,230,211,0.5)', display: 'block', marginBottom: '6px' }}>
                    {t('claim.email_label')} *
                  </label>
                  <input
                    style={inputStyle}
                    type="email"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder={t('claim.email_placeholder')}
                    required
                    data-testid="claim-email-input"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(245,230,211,0.5)', display: 'block', marginBottom: '6px' }}>
                    {t('claim.allinx_label')} *
                  </label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="numeric"
                    value={form.allinx_id}
                    onChange={e => setForm(p => ({ ...p, allinx_id: e.target.value.replace(/\D/g, '') }))}
                    placeholder={t('claim.allinx_placeholder')}
                    required
                    data-testid="claim-allinx-input"
                  />
                </div>

                {error && (
                  <div style={{ padding: '14px 16px', background: 'rgba(228,93,93,0.1)', border: '1px solid rgba(228,93,93,0.3)', borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <AlertCircle size={18} style={{ color: '#E45D5D', flexShrink: 0, marginTop: '1px' }} />
                    <p style={{ color: '#E45D5D', fontSize: '0.88rem', margin: 0, lineHeight: 1.5 }}>{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '14px', background: loading ? 'rgba(212,175,55,0.3)' : '#D4AF37',
                    border: 'none', borderRadius: '12px', color: '#05080F',
                    fontFamily: 'Montserrat,sans-serif', fontSize: '0.92rem', fontWeight: 700,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginTop: '4px',
                  }}
                  data-testid="claim-submit-btn"
                >
                  {loading ? t('claim.submitting') : t('claim.submit')}
                </button>
              </form>

              {/* Note */}
              <p style={{ color: 'rgba(245,230,211,0.35)', fontSize: '0.75rem', marginTop: '20px', lineHeight: 1.6, textAlign: 'center' }}>
                {t('claim.note')}
              </p>
            </>
          ) : (
            /* Success */
            <div style={{ textAlign: 'center' }}>
              {result.is_jackpot ? (
                <>
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🌟</div>
                  <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '2rem', color: '#FFD700', marginBottom: '16px' }}>
                    {t('claim.success_title')}
                  </h2>
                  <p style={{ color: '#F5E6D3', fontSize: '1rem', lineHeight: 1.7, marginBottom: '24px' }}>
                    {t('claim.success_jackpot')}
                  </p>
                </>
              ) : (
                <>
                  <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(59,190,138,0.15)', border: '2px solid #3BBE8A', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <CheckCircle size={32} style={{ color: '#3BBE8A' }} />
                  </div>
                  <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.8rem', color: '#3BBE8A', marginBottom: '14px' }}>
                    {t('claim.success_title')}
                  </h2>
                  <p style={{ color: '#F5E6D3', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '24px' }}>
                    {t('claim.success_body')
                      .replace('{name}', result.first_name)
                      .replace('{tokens}', result.result_value)}
                  </p>
                </>
              )}
              <Link to="/" style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '12px 28px', background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.4)', borderRadius: '50px',
                color: '#D4AF37', textDecoration: 'none',
                fontFamily: 'Montserrat,sans-serif', fontSize: '0.85rem', fontWeight: 600,
              }}>
                <ArrowLeft size={14} /> {t('claim.back')}
              </Link>
            </div>
          )}
        </div>

        {/* Back link */}
        {!result && (
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '24px', color: 'rgba(245,230,211,0.4)', fontSize: '0.8rem', textDecoration: 'none' }}>
            <ArrowLeft size={13} /> {t('claim.back')}
          </Link>
        )}
      </div>
    </div>
  );
}
